import {
  DocumentData,
  Query,
  QueryConstraint,
  Timestamp,
  collection,
  doc,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { auth, db, functionsUrl } from "../firebase/firebase";
import { collectionData, docData } from "rxfire/firestore";
import { Observable, map, of } from "rxjs";
import DataTransformationService from "./data-transformation.service";
import axios from "axios";
import { ProductVariant } from "../models/product-variant";

function generateId() {
  return doc(collection(db, "productVariants")).id;
}

function getOne(id: string): Observable<ProductVariant> {
  if (!id) return of(undefined);
  const docRef = doc(db, `productVariants/${id}`);
  return docData(docRef).pipe(
    map((firestoreDocument) =>
      DataTransformationService.convertTimestampsToIsoString(firestoreDocument)
    )
  );
}

function getAllByProduct(
  tenantId: string,
  brandId: string,
  productId: string
): Observable<ProductVariant[]> {
  const collectionRef = collection(db, "productVariants");

  const queryConstraint: QueryConstraint[] = [
    where("tenantId", "==", tenantId),
    where("brandId", "==", brandId),
    where("productId", "==", productId),
    where("deleted", "==", false),
  ];

  const collectionQuery: Query<DocumentData> = query(
    collectionRef,
    ...queryConstraint
  );

  return collectionData(collectionQuery).pipe(
    map((collection) => {
      //sort by order
      collection.sort(
        (a, b) => (a.order ? a.order : 0) - (b.order ? b.order : 0)
      );
      //convert timestamps to isostring
      collection = collection.map((firestoreDocument) =>
        DataTransformationService.convertTimestampsToIsoString(
          firestoreDocument
        )
      );
      return collection;
    })
  );
}

function getAllByProducts(
  tenantId: string,
  brandId: string,
  productIds: string[]
): Observable<ProductVariant[]> {
  //Only query with valid productIds array
  if (!productIds || !Array.isArray(productIds) || productIds.length === 0)
    return of([]);

  //Limit query due to firebase limitation
  if (productIds.length > 10)
    throw new Error("Currently limited to ten products.");

  const collectionRef = collection(db, "productVariants");

  const queryConstraint: QueryConstraint[] = [
    where("tenantId", "==", tenantId),
    where("brandId", "==", brandId),
    where("productId", "in", productIds),
    where("deleted", "==", false),
  ];

  const collectionQuery: Query<DocumentData> = query(
    collectionRef,
    ...queryConstraint
  );

  return collectionData(collectionQuery).pipe(
    map((collection) => {
      //sort by order
      collection.sort(
        (a, b) =>
          (a.createdDate && a.createdDate instanceof Timestamp
            ? a.createdDate.seconds
            : 99999999999) -
          (b.createdDate && b.createdDate instanceof Timestamp
            ? b.createdDate.seconds
            : 99999999999)
      );
      //convert timestamps to isostring
      collection = collection.map((firestoreDocument) =>
        DataTransformationService.convertTimestampsToIsoString(
          firestoreDocument
        )
      );
      return collection;
    })
  );
}

async function saveOne(
  productVariant: Partial<ProductVariant>,
  isNew: boolean
): Promise<any> {
  try {
    if (isNew) {
      productVariant.createdDate = serverTimestamp();
      productVariant.createdByUserAccountId = auth.currentUser.uid;
    } else {
      productVariant.updatedDate = serverTimestamp();
      productVariant.updatedByUserAccountId = auth.currentUser.uid;
    }

    if (productVariant.deleted == undefined) productVariant.deleted = false;

    productVariant =
      DataTransformationService.convertDatesToFirestoreTimestamp(
        productVariant
      );

    let docRef = doc(db, `productVariants/${productVariant.id}`);
    await setDoc(docRef, productVariant, { merge: true });
    return;
  } catch (err) {
    throw err;
  }
}

async function deleteOne(productVariantId: string): Promise<any> {
  try {
    let docRef = doc(db, `productVariants/${productVariantId}`);
    const documentToDelete = {
      deleted: true,
      deletedDate: serverTimestamp(),
      updatedDate: serverTimestamp(),
      updatedByUserAccountId: auth.currentUser.uid,
    };
    await setDoc(docRef, documentToDelete, { merge: true });
    return;
  } catch (err) {
    throw err;
  }
}

// Uplaods file and returns the file url
async function uploadProductVariantFile(
  brandId: string,
  productVariantId: string,
  file: File
): Promise<string> {
  try {
    //configure axios client with bearer token
    const token = await auth.currentUser.getIdToken();
    const client = axios.create({
      baseURL: `${functionsUrl}`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    //get signed url from firebase function
    const apiResponse = await client.post(
      `brand/${brandId}/product-variants/r2-signed-url`,
      {
        productVariantId: productVariantId,
        fileName: file.name,
      }
    );

    const signedUrlAndFileUrl: { signedUrl: string; fileUrl: string } =
      apiResponse?.data;
    if (!signedUrlAndFileUrl)
      throw new Error("Error getting signed url during upload process.");

    //upload image to r2
    await axios.put(signedUrlAndFileUrl.signedUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
    });

    return signedUrlAndFileUrl.fileUrl;
  } catch (err) {
    throw err;
  }
}

const ProductVariantService = {
  generateId,
  getOne,
  getAllByProduct,
  getAllByProducts,
  saveOne,
  deleteOne,
  uploadProductVariantFile,
};

export default ProductVariantService;
